import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  FetchFinanceCategoryTotalsArgs,
  FetchFinanceEntriesArgs,
  FetchFinanceNetResultArgs,
} from '../api/types/financeEntries.types';

import {
  fetchFinanceCategoryTotals,
  fetchFinanceNetResult,
  fetchFinanceEntries,
} from '../api/financeEntries.api';

/**
 * T17 (Nivo 4) — two stores, both keyed by the ORGANIZATION first.
 *
 * That ordering is what makes `[...fetchNetResult._def, organizationId]` a
 * usable invalidation handle: a mutation refreshes every cached month range and
 * every cached list page of ITS organization and touches no other one's.
 *
 * No `tenantId` in either key, unlike `financeStats.queryKeys`: those keys carry
 * one because the platform page's own arguments do (`'all'` = cross-tenant).
 * These routes take the tenant from the session and the organization from the
 * path — a super admin who selects another tenant leaves this organization
 * altogether, and organization ids do not repeat across tenants.
 *
 * The net-result key no longer carries `tz`: the server buckets the LEDGER
 * half of every month in the ORGANIZATION's own zone (Foundation 1, D6/D7),
 * so every viewer of the same organization is looking at the same cut.
 *
 * The selected currency is deliberately NOT in either key. The statement is
 * fetched for ALL currencies in one request and the selector only hides blocks
 * (Global Constraint 9 — currencies are never summed), so folding it in would
 * cache one identical response per currency and refetch on every selection.
 */
export const financeEntriesQueryKeys = createQueryKeys('financeEntries', {
  fetchEntries: (args: FetchFinanceEntriesArgs) => ({
    queryFn: () => fetchFinanceEntries(args),
    queryKey: [
      args.organizationId,
      args.filters ?? {},
      args.page,
      args.pageSize,
    ],
  }),
  // Task 5.2 — organization first, like the other two, so an entry mutation
  // refreshes the category lines of every cached window of ITS organization.
  fetchCategoryTotals: (args: FetchFinanceCategoryTotalsArgs) => ({
    queryFn: () => fetchFinanceCategoryTotals(args),
    queryKey: [args.organizationId, args.from, args.to],
  }),
  fetchNetResult: (args: FetchFinanceNetResultArgs) => ({
    queryFn: () => fetchFinanceNetResult(args),
    queryKey: [args.organizationId, args.from, args.to],
  }),
});
